import { Checkbox, Chip } from '@mui/material';
import { CSSProperties, useState } from 'react';
import { reject, append } from 'ramda';
import classNames from 'classnames';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { SvgIcon } from '@progress/kendo-react-common';
import { searchIcon } from '@progress/kendo-svg-icons';
import styles from './_denial-reasons-editor.module.scss';
import EditorCard from '../EditorCard/EditorCard';
import { IDealerServiceDecisionReasonCode } from '../../../Interfaces/Common/IDealerServiceDecisionCodeReason';
import { filterByLabel } from '../../../utils/decisionDialogUtils';

export interface DenialReasonsEditorProps {
  onChange: (reasons: IDealerServiceDecisionReasonCode[]) => void;
  reasons: IDealerServiceDecisionReasonCode[];
  selectedItems: IDealerServiceDecisionReasonCode[];
  style?: CSSProperties;
  /** Applied to the scrolling reason grid. */
  listStyle?: CSSProperties;
  /** Single scrolling column with no filter box, for when this editor shares a row with the stipulations editor. */
  compact?: boolean;
  /** Inline validation shown in the card header. */
  errorMessage?: string;
}

export default function DenialReasonsEditor(props: DenialReasonsEditorProps) {
  const [filterText, setFilterText] = useState('');

  const isReasonChecked = (reasonId: string | number) => props.selectedItems.some(reason => reason.id === reasonId);

  const toggleReason = (reason: IDealerServiceDecisionReasonCode) => {
    const selectedReasons = isReasonChecked(reason.id)
      ? reject((r: IDealerServiceDecisionReasonCode) => r.id === reason.id, props.selectedItems)
      : append(reason, props.selectedItems);
    props.onChange(selectedReasons);
  };

  const visibleReasons = filterByLabel(props.reasons, filterText, reason => reason.description);

  return (
    <EditorCard
      title="Adverse Action Reasons"
      count={props.selectedItems.length}
      errorMessage={props.errorMessage}
      titleStyle={props.style}
      className={styles.denialReasonsEditorContainer}
      actions={
        !props.compact && (
          <TextBox
            className={styles.filterBox}
            size="small"
            value={filterText}
            onChange={(e: TextBoxChangeEvent) => setFilterText((e.target.value as string) ?? '')}
            placeholder="Filter reasons"
            prefix={() => <SvgIcon icon={searchIcon} className={styles.filterIcon} />}
          />
        )
      }
    >
      <div className={classNames(styles.selectedRow, { [styles.selectedRowCompact]: props.compact })}>
        <span className={styles.selectedLabel}>Sending</span>
        {props.selectedItems.length === 0 && <span className={styles.selectedEmpty}>Nothing selected yet</span>}
        {props.selectedItems.map(reason => (
          <Chip
            key={reason.id}
            size="small"
            className={styles.selectedChip}
            label={reason.description}
            onDelete={() => toggleReason(reason)}
          />
        ))}
      </div>
      <div className={props.compact ? styles.reasonGridCompact : styles.reasonGrid} style={props.listStyle}>
        {visibleReasons.map((reason: IDealerServiceDecisionReasonCode) => {
          const checked = isReasonChecked(reason.id);
          // A native label wrapping the checkbox keeps the input focusable and associates the text with it.
          return (
            <label key={reason.id} className={classNames(styles.reasonRow, { [styles.reasonRowChecked]: checked })}>
              <Checkbox
                size="small"
                edge="start"
                checked={checked}
                onChange={() => toggleReason(reason)}
                disableRipple
                className={styles.rowCheckbox}
              />
              <span className={styles.rowLabel}>{reason.description}</span>
            </label>
          );
        })}
        {visibleReasons.length === 0 && props.reasons.length > 0 && (
          <span className={styles.noMatches}>No reasons match "{filterText.trim()}"</span>
        )}
      </div>
    </EditorCard>
  );
}
